/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { Box, Typography, Grid, TextField, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CustomTextField, SelectDropdown } from '../../index';
import { useState } from 'react';

const sectionHeaderStyle = {
  color: '#84d8c5',
  fontWeight: 700,
  mb: 1,
  mt: 3,
};

export const PersonalInfoTab = (props) => {
  const handleChange = (eventOrValue, name) => {
    const value = eventOrValue?.target
      ? eventOrValue.target.value
      : eventOrValue;

    props.setPersonalInfo((prev) => {
      let newValue = value;

      if (['height', 'weight', 'age'].includes(name)) {
        newValue = value === '' ? '' : parseFloat(value);
      }

      return {
        ...prev,
        [name]: newValue,
      };
    });
  };
  const [startTouched, setStartTouched] = useState(false);

  return (
    <Box
      sx={{
        maxHeight: '95%',
        overflowY: 'auto',
        padding: 2,
        bgcolor: 'rgb(26, 32, 44, 0.6)',
        borderRadius: '5px',
        mt: 1,
      }}
    >
      <Typography variant="h5" sx={sectionHeaderStyle}>
        Basic Information
      </Typography>
      {/* ROW 1 */}
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <CustomTextField
            // error={!props.personalInfo.nickname}
            errorMsg={'Please enter a nickname name'}
            label="Nickname"
            value={props.personalInfo.nickname}
            onChange={(event) => handleChange(event, 'nickname')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomTextField
            type="number"
            errorMsg={'Please enter the age of the employee.'}
            label="Age"
            value={props.personalInfo.age}
            onChange={(event) => handleChange(event, 'age')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectDropdown
            error={!props.personalInfo.sex}
            fullWidth={true}
            margin="normal"
            label="Sex"
            value={props.personalInfo.sex}
            helperText={!props.personalInfo.sex ? 'Select a role' : ''}
            onChange={(event) => handleChange(event, 'sex')}
            viewOnly={props.viewOnly}
            required
          >
            <MenuItem value={'Male'}>Male</MenuItem>
            <MenuItem value={'Female'}>Female</MenuItem>
          </SelectDropdown>
        </Grid>
      </Grid>
      {/* ROW 2 */}
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date of Birth"
              value={
                props.personalInfo.dateofbirth
                  ? new Date(props.personalInfo.dateofbirth)
                  : null
              }
              onChange={(newValue) => handleChange(newValue, 'dateofbirth')}
              disableOpenPicker={props.viewOnly}
              readOnly={props.viewOnly}
              maxDate={new Date()}
              renderInput={(params) => {
                const showError =
                  startTouched && !props.personalInfo.dateofbirth;

                return (
                  <TextField
                    onFocus={() => setStartTouched(true)}
                    margin="normal"
                    required
                    fullWidth
                    {...params}
                    error={showError}
                    helperText={showError ? 'Date of Birth is required' : ''}
                    inputProps={{
                      ...params.inputProps,
                      style: { cursor: props.viewOnly ? 'default' : undefined },
                    }}
                  />
                );
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={4}>
          <CustomTextField
            // error={!lastname}
            errorMsg={'Please enter a valid place of birth.'}
            label="Place of Birth"
            value={props.personalInfo.placeofbirth}
            onChange={(event) => handleChange(event, 'placeofbirth')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomTextField
            // error={!lastname}
            errorMsg={'Please enter a valid blood type.'}
            label="Blood type"
            value={props.personalInfo.bloodtype}
            onChange={(event) => handleChange(event, 'bloodtype')}
            viewOnly={props.viewOnly}
          />
        </Grid>
      </Grid>

      {/* ROW 3 */}
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <SelectDropdown
            required
            fullWidth={true}
            margin="normal"
            label="Civil Status"
            value={props.personalInfo.civilstatus}
            onChange={(event) => handleChange(event, 'civilstatus')}
            viewOnly={props.viewOnly}
          >
            <MenuItem value={'Single'}>Single</MenuItem>
            <MenuItem value={'Married'}>Married</MenuItem>
            <MenuItem value={'Widowed'}>Widowed</MenuItem>
            <MenuItem value={'Separated'}>Separated</MenuItem>
            <MenuItem value={'Annulled'}>Annulled</MenuItem>
            <MenuItem value={'Divorced'}>Divorced</MenuItem>
          </SelectDropdown>
        </Grid>
        <Grid item xs={4}>
          <CustomTextField
            error={!props.personalInfo.citizenship}
            errorMsg={'Please enter a valid citizenship'}
            label="Citizenship"
            value={props.personalInfo.citizenship}
            onChange={(event) => handleChange(event, 'citizenship')}
            viewOnly={props.viewOnly}
            required
          />
        </Grid>
        <Grid item xs={2}>
          <CustomTextField
            // error={!lastname}
            type="number"
            errorMsg={'Please enter a valid height.'}
            label="Height (cm)"
            value={props.personalInfo.height}
            onChange={(event) => handleChange(event, 'height')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={2}>
          <CustomTextField
            // error={!lastname}
            type="number"
            errorMsg={'Please enter a valid weight.'}
            label="Weight (kg)"
            value={props.personalInfo.weight}
            onChange={(event) => handleChange(event, 'weight')}
            viewOnly={props.viewOnly}
          />
        </Grid>
      </Grid>

      <Typography variant="h5" sx={sectionHeaderStyle}>
        Contact Information
      </Typography>
      {/* ROW 4 */}
      <Grid container spacing={4}>
        <Grid item xs={6}>
          <CustomTextField
            error={!props.personalInfo.cellphone}
            errorMsg={'Please enter a valid cellphone number.'}
            label="Cellphone Number"
            value={props.personalInfo.cellphone}
            onChange={(event) => handleChange(event, 'cellphone')}
            viewOnly={props.viewOnly}
            required
          />
        </Grid>
      </Grid>

      <Typography variant="h5" sx={sectionHeaderStyle}>
        Government IDs
      </Typography>
      {/* ROW 5 */}
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <CustomTextField
            // error={!firstname}
            errorMsg={'Please enter a valid passport number.'}
            label="Passport No."
            value={props.personalInfo.passport}
            onChange={(event) => handleChange(event, 'passport')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomTextField
            // error={!lastname}
            errorMsg={'Please enter a valid SSS No.'}
            label="SSS No."
            value={props.personalInfo.sss}
            onChange={(event) => handleChange(event, 'sss')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomTextField
            // error={!lastname}
            errorMsg={'Please enter a valid Pag-Ibig ID.'}
            label="Pag-Ibig ID"
            value={props.personalInfo.pagibig}
            onChange={(event) => handleChange(event, 'pagibig')}
            viewOnly={props.viewOnly}
          />
        </Grid>
      </Grid>
      {/* ROW 6 */}
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <CustomTextField
            // error={!firstname}
            errorMsg={'Please enter a valid Phil-Health id.'}
            label="PhilHealth No."
            value={props.personalInfo.philhealth}
            onChange={(event) => handleChange(event, 'philhealth')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomTextField
            // error={!lastname}
            errorMsg={'Please enter a valid GSIS No.'}
            label="GSIS No."
            value={props.personalInfo.gsis}
            onChange={(event) => handleChange(event, 'gsis')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomTextField
            // error={!lastname}
            errorMsg={'Please enter a valid TIN'}
            label="TIN No."
            value={props.personalInfo.tin}
            onChange={(event) => handleChange(event, 'tin')}
            viewOnly={props.viewOnly}
          />
        </Grid>
      </Grid>

      <Typography variant="h5" sx={sectionHeaderStyle}>
        Others
      </Typography>
      {/* ROW z */}
      <Grid container spacing={4}>
        <Grid item xs={6}>
          <CustomTextField
            // error={!firstname}
            errorMsg={'Please enter your skills'}
            label="Skills"
            value={props.personalInfo.skills}
            onChange={(event) => handleChange(event, 'skills')}
            viewOnly={props.viewOnly}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomTextField
            // error={!lastname}
            errorMsg={'Please enter your hobbies'}
            label="Hobbies"
            value={props.personalInfo.hobbies}
            onChange={(event) => handleChange(event, 'hobbies')}
            viewOnly={props.viewOnly}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
